import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { CheckCircle2, AlertCircle, Moon, Loader2, TrendingUp, TrendingDown, Activity } from 'lucide-react';
import { motion } from 'framer-motion';
import type { CoachDecisionV2, CoachDecision, ReadinessData } from '@/types/coachDecision';
import { adaptLegacyDecision } from '@/lib/ai/adapters/adaptLegacyDecision';
import { MetricInfoButton } from '@/components/MetricInfoButton';

/** Intelligence data structure from the API (supports both v1 and v2) */
interface IntelligenceData {
  recommendation?: string | null;
  explanation?: string | null;
  confidence?: {
    score: number;
    explanation?: string;
  } | null;
  version?: string;
  decision?: string;
  primary_focus?: string;
  signals?: string[];
  recommended_focus?: string[];
}

interface DailyDecisionCardProps {
  todayIntelligence?: CoachDecision | IntelligenceData | null;
  isLoading?: boolean;
  error?: unknown;
  /** When true, shows only verdict + sparkline (used for sticky mobile HUD) */
  compact?: boolean;
  className?: string;
}

type DecisionType = 'REST' | 'PROCEED' | 'MODIFY' | 'CANCEL';

const mapDecisionToType = (decision: string): DecisionType => {
  const upper = decision.toUpperCase();
  if (upper === 'REST' || upper === 'PROCEED' || upper === 'MODIFY' || upper === 'CANCEL') {
    return upper as DecisionType;
  }
  const lower = decision.toLowerCase();
  if (lower.includes('rest') || lower.includes('recovery')) return 'REST';
  if (lower.includes('easy_with_caution') || lower.includes('moderate_with_caution')) return 'MODIFY';
  if (lower.includes('modify') || lower.includes('adjust')) return 'MODIFY';
  if (lower.includes('cancel') || lower.includes('skip')) return 'CANCEL';
  return 'PROCEED';
};

const decisionConfig: Record<DecisionType, {
  icon: typeof CheckCircle2;
  displayKey: string;
  glowClass: string;
  dotClass: string;
  colorClass: string;
  readinessKey: string;
}> = {
  PROCEED: {
    icon: CheckCircle2,
    displayKey: 'verdict.proceed',
    glowClass: 'glow-proceed',
    dotClass: 'telemetry-dot--green',
    colorClass: 'text-green-400',
    readinessKey: 'verdict.readinessHigh',
  },
  MODIFY: {
    icon: AlertCircle,
    displayKey: 'verdict.modify',
    glowClass: 'glow-modify',
    dotClass: 'telemetry-dot--amber',
    colorClass: 'text-amber-400',
    readinessKey: 'verdict.readinessModerate',
  },
  CANCEL: {
    icon: AlertCircle,
    displayKey: 'verdict.cancel',
    glowClass: 'glow-cancel',
    dotClass: 'telemetry-dot--red',
    colorClass: 'text-red-400',
    readinessKey: 'verdict.readinessLow',
  },
  REST: {
    icon: Moon,
    displayKey: 'verdict.restDay',
    glowClass: 'glow-rest',
    dotClass: 'telemetry-dot--amber',
    colorClass: 'text-amber-400',
    readinessKey: 'verdict.recoveryProtocol',
  },
};

function normalizeToV2(data: CoachDecision | IntelligenceData | null | undefined): CoachDecisionV2 | null {
  if (!data) return null;
  
  if (typeof data === 'object' && 'version' in data && data.version === 'coach_decision_v2') {
    return data as CoachDecisionV2;
  }
  
  if (typeof data === 'object' && 'version' in data && data.version === 'coach_decision_v2' && 'decision' in data && 'signals' in data && 'recommended_focus' in data) {
    const intelData = data as IntelligenceData;
    return {
      version: 'coach_decision_v2',
      decision: mapDecisionToType(intelData.decision || '') as 'REST' | 'PROCEED' | 'MODIFY' | 'CANCEL',
      primary_focus: intelData.primary_focus || 'Execute today appropriately',
      confidence: typeof intelData.confidence === 'object' && typeof intelData.confidence?.score === 'number'
        ? intelData.confidence.score
        : 0.8,
      signals: intelData.signals || [],
      recommended_focus: intelData.recommended_focus || [],
      explanation: intelData.explanation || undefined,
    };
  }
  
  if (typeof data === 'object' && 'recommendation' in data && data.recommendation) {
    const v1Data = data as { recommendation: string; explanation?: string; confidence?: { score?: number; explanation?: string } | number };
    const v1Decision = {
      recommendation: v1Data.recommendation,
      explanation: 'explanation' in v1Data && v1Data.explanation ? v1Data.explanation : v1Data.recommendation || '',
      confidence: {
        score: typeof v1Data.confidence === 'object' && typeof v1Data.confidence?.score === 'number'
          ? v1Data.confidence.score
          : typeof v1Data.confidence === 'number'
          ? v1Data.confidence
          : 0.8,
        explanation: typeof v1Data.confidence === 'object' && typeof v1Data.confidence?.explanation === 'string'
          ? v1Data.confidence.explanation
          : 'Based on current training state',
      },
    };
    return adaptLegacyDecision(v1Decision);
  }
  
  return null;
}

/** Readiness category display config */
const readinessConfig: Record<string, { label: string; colorClass: string; dotClass: string }> = {
  excellent: { label: 'EXCELLENT', colorClass: 'text-green-400', dotClass: 'bg-green-400' },
  good: { label: 'GOOD', colorClass: 'text-emerald-400', dotClass: 'bg-emerald-400' },
  fair: { label: 'FAIR', colorClass: 'text-amber-400', dotClass: 'bg-amber-400' },
  poor: { label: 'POOR', colorClass: 'text-red-400', dotClass: 'bg-red-400' },
};

/** Closed-loop readiness badge (replaces static readiness label when data available) */
function ReadinessBadge({ data, fallbackKey }: { data: CoachDecision | IntelligenceData; fallbackKey: string }) {
  const { t } = useTranslation();
  const readiness = data as Partial<ReadinessData>;
  const category = readiness.readiness_category?.toLowerCase();
  const score = readiness.readiness_score;

  if (!category || score == null) {
    return (
      <span className="text-[9px] font-mono tracking-[0.12em] uppercase text-slate-600">
        {t(fallbackKey)}
      </span>
    );
  }

  const cfg = readinessConfig[category] || readinessConfig.fair;

  return (
    <div className="flex items-center gap-2">
      <div className={cn('h-1.5 w-1.5 rounded-full', cfg.dotClass)} />
      <span className={cn('text-[9px] font-mono tracking-[0.12em] uppercase', cfg.colorClass)}>
        {cfg.label} {Math.round(score)}
      </span>
      <MetricInfoButton metricKey="readiness" />
    </div>
  );
}

/** Mini sparkline SVG for readiness visualization */
function ReadinessSparkline({ decision }: { decision: DecisionType }) {
  const colors: Record<DecisionType, string> = {
    PROCEED: 'hsl(150, 60%, 50%)',
    MODIFY: 'hsl(35, 90%, 55%)',
    CANCEL: 'hsl(0, 70%, 55%)',
    REST: 'hsl(35, 90%, 55%)',
  };
  const color = colors[decision];
  
  // Generate a mini sparkline based on decision type
  const points = decision === 'PROCEED' 
    ? 'M0,24 Q15,22 30,18 Q45,14 60,10 Q75,8 90,6 Q105,5 120,4'
    : decision === 'REST'
    ? 'M0,8 Q15,10 30,14 Q45,18 60,20 Q75,22 90,23 Q105,24 120,24'
    : 'M0,12 Q15,14 30,16 Q45,18 60,14 Q75,16 90,18 Q105,20 120,18';

  return (
    <svg viewBox="0 0 120 28" className="w-full h-7" preserveAspectRatio="none">
      <defs>
        <linearGradient id={`spark-${decision}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={color} stopOpacity="0.2" />
          <stop offset="50%" stopColor={color} stopOpacity="0.8" />
          <stop offset="100%" stopColor={color} stopOpacity="0.4" />
        </linearGradient>
        <filter id={`sparkGlow-${decision}`}>
          <feGaussianBlur stdDeviation="2" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      {/* Reference line */}
      <line x1="0" y1="14" x2="120" y2="14" stroke="hsl(215, 20%, 20%)" strokeWidth="0.5" strokeDasharray="2 4" />
      {/* Signal path */}
      <path d={points} fill="none" stroke={`url(#spark-${decision})`} strokeWidth="1.5" filter={`url(#sparkGlow-${decision})`} />
      {/* Endpoint dot */}
      <circle cx="120" cy={decision === 'PROCEED' ? 4 : decision === 'REST' ? 24 : 18} r="2" fill={color} opacity="0.9" />
    </svg>
  );
}

/** Loading skeleton */
function DecisionSkeleton({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <div className={cn('rounded-lg border border-white/[0.06] bg-white/[0.02] backdrop-blur-sm p-6', className)}>
      <div className="flex items-center gap-3 mb-4">
        <div className="telemetry-dot telemetry-dot--blue" />
        <span className="text-[10px] font-mono tracking-[0.15em] uppercase text-slate-600">
          {t('verdict.analyzingSignals')}
        </span>
      </div>
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-slate-600" />
      </div>
    </div>
  );
}

/** Unavailable state */
function DecisionUnavailable({ message, className }: { message: string; className?: string }) {
  const { t } = useTranslation();
  return (
    <div className={cn('rounded-lg border border-white/[0.06] bg-white/[0.02] backdrop-blur-sm p-6', className)}>
      <div className="flex items-center gap-3 mb-2">
        <div className="telemetry-dot telemetry-dot--blue" />
        <span className="text-[10px] font-mono tracking-[0.15em] uppercase text-slate-600">
          {t('verdict.label')}
        </span>
      </div>
      <p className="text-sm font-mono text-slate-500">{message}</p>
    </div>
  );
}

export function DailyDecisionCard({ todayIntelligence, isLoading = false, error, compact = false, className }: DailyDecisionCardProps) {
  const { t } = useTranslation();
  const data = todayIntelligence;

  if (isLoading) {
    return <DecisionSkeleton className={className} />;
  }

  const isServiceUnavailable = error && typeof error === 'object' && 'status' in error && (error as { status?: number }).status === 503;

  if (isServiceUnavailable) {
    return <DecisionUnavailable message={t('verdict.analyzingData')} className={className} />;
  }

  if (error || !data) {
    return <DecisionUnavailable message={t('verdict.unavailable')} className={className} />;
  }

  const confidenceObj = typeof data.confidence === 'object' && data.confidence !== null ? data.confidence : null;
  const confidenceScore = confidenceObj?.score ?? (typeof data.confidence === 'number' ? data.confidence : null);
  const confidenceExplanation = confidenceObj?.explanation ?? null;
  
  const isPlaceholder = 
    (confidenceScore === 0.0 && 
     (confidenceExplanation === "Decision not yet generated" || 
      ('explanation' in data && data.explanation === "The coach is still analyzing your training data. Recommendations will be available soon."))) ||
    ('explanation' in data && data.explanation === "The coach is still analyzing your training data. Recommendations will be available soon.") ||
    (confidenceExplanation === "Decision not yet generated");

  if (isPlaceholder) {
    return <DecisionUnavailable message={t('verdict.analyzingData')} className={className} />;
  }

  const v2Decision = normalizeToV2(data);
  
  if (!v2Decision) {
    return <DecisionUnavailable message={t('verdict.unavailable')} className={className} />;
  }
  
  const decisionType = v2Decision.decision;
  const config = decisionConfig[decisionType];
  const Icon = config.icon;
  
  const signals = v2Decision.signals && v2Decision.signals.length > 0
    ? v2Decision.signals.slice(0, 3)
    : [];

  const getSignalGlow = (signal: string): { icon: typeof TrendingUp; glowClass: string; colorClass: string } => {
    const lower = signal.toLowerCase();
    if (lower.includes('rest') || lower.includes('recovery') || lower.includes('fatigue') || lower.includes('elevated')) {
      return { icon: TrendingDown, glowClass: 'signal-glow-amber', colorClass: 'text-amber-400' };
    }
    if (lower.includes('completed') || lower.includes('good') || lower.includes('ready') || lower.includes('strong')) {
      return { icon: TrendingUp, glowClass: 'signal-glow-green', colorClass: 'text-green-400' };
    }
    return { icon: Activity, glowClass: 'signal-glow-blue', colorClass: 'text-blue-400' };
  };

  return (
    <motion.div
      className={cn('relative rounded-lg border border-white/[0.06] bg-white/[0.02] backdrop-blur-sm overflow-hidden', className)}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      {/* Accent edge glow */}
      <div className={cn(
        'absolute left-0 top-0 bottom-0 w-[2px]',
        decisionType === 'PROCEED' ? 'bg-green-500 shadow-[0_0_8px_hsl(150,60%,50%,0.4)]' :
        decisionType === 'REST' || decisionType === 'MODIFY' ? 'bg-amber-500 shadow-[0_0_8px_hsl(35,90%,55%,0.4)]' :
        'bg-red-500 shadow-[0_0_8px_hsl(0,70%,55%,0.4)]',
      )} />

      <div className={cn(compact ? 'p-3 pl-5' : 'p-5 pl-6')}>
        {/* Header row: label + readiness sparkline */}
        <div className={cn('flex items-center justify-between', compact ? 'mb-2' : 'mb-4')}>
          <div className="flex items-center gap-3">
            <div className={cn('telemetry-dot', config.dotClass)} />
            <span className="text-[10px] font-mono tracking-[0.15em] uppercase text-slate-500">
              {t('verdict.label')}
            </span>
            <MetricInfoButton metricKey="dailyVerdict" />
          </div>
          <ReadinessBadge data={data} fallbackKey={config.readinessKey} />
        </div>

        {/* Hero verdict + sparkline in compact row on mobile */}
        <div className={cn(compact ? 'flex items-center gap-4' : '')}>
          <div className={cn('flex items-center gap-4', compact ? 'mb-0' : 'mb-4')}>
            <Icon className={cn(compact ? 'h-5 w-5' : 'h-6 w-6', config.colorClass)} strokeWidth={1.5} />
            <h2 className={cn(
              'font-mono font-light tracking-tight',
              compact ? 'text-2xl' : 'text-3xl md:text-4xl',
              config.colorClass,
              config.glowClass,
            )}>
              {t(config.displayKey)}
            </h2>
          </div>

          {/* Readiness sparkline */}
          <div className={cn(compact ? 'flex-1 max-w-[140px]' : 'mb-5 max-w-xs')}>
            <ReadinessSparkline decision={decisionType} />
          </div>
        </div>

        {/* Recovery Signals & Focus — hidden in compact mode */}
        {!compact && signals.length > 0 && (
          <div className="mb-5">
            <div className="text-[9px] font-mono tracking-[0.15em] uppercase text-slate-600 mb-2">
              {t('verdict.recoverySignals')}
            </div>
            <div className="flex flex-wrap gap-2">
              {signals.map((signal, index) => {
                const sg = getSignalGlow(signal);
                const SignalIcon = sg.icon;
                return (
                  <div
                    key={index}
                    className={cn(
                      'inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-white/[0.06] bg-white/[0.03]',
                      'text-[11px] font-mono',
                      sg.colorClass,
                    )}
                  >
                    <SignalIcon className={cn('h-3 w-3', sg.glowClass)} />
                    <span>{signal}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Readiness limiting factor */}
        {!compact && (data as Partial<ReadinessData>).readiness_limiting_factor && (
          <div className="mb-5">
            <div className="text-[9px] font-mono tracking-[0.15em] uppercase text-slate-600 mb-1.5">
              {t('verdict.limitingFactor', 'LIMITING FACTOR')}
            </div>
            <p className="text-[12px] font-mono text-amber-400/80">
              {(data as Partial<ReadinessData>).readiness_limiting_factor}
            </p>
          </div>
        )}

        {/* Today's Focus */}
        {!compact && v2Decision.recommended_focus && v2Decision.recommended_focus.length > 0 && (
          <div>
            <div className="text-[9px] font-mono tracking-[0.15em] uppercase text-slate-600 mb-2">
              {t('verdict.todaysFocus')}
            </div>
            <ul className="space-y-1.5">
              {v2Decision.recommended_focus.map((item, index) => (
                <li key={index} className="text-[13px] font-mono text-slate-300 flex items-start gap-2">
                  <span className="text-green-500 mt-0.5 flex-shrink-0 signal-glow-green">✔</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </motion.div>
  );
}
